import { AvatarBlock, Link } from '@ksyos/design-system';
import { ManagerAuthorization } from '@common/enums';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization, useUiStateContext } from '@frontend/utils';
import type { IOrganization } from '../../interfaces/organization';
import type { IPractitioner } from '../../interfaces/practitioner';

export interface IPersonalSettingsProfileProps {
    isActiveEmployment: boolean;
    organization: IOrganization;
    practitioner: IPractitioner;
    size: 'large' | 'small';
}

export function PersonalSettingsProfile(props: IPersonalSettingsProfileProps) {
    const { uiState, dispatchUiStateAction } = useUiStateContext();

    const isManager = hasOneAuthorization([
        ManagerAuthorization.employmentSettingsRead,
        ManagerAuthorization.employmentSettingsEdit,
    ]);

    function handleLinkClick(event: React.MouseEvent<HTMLElement, MouseEvent>) {
        event.stopPropagation();
        if (uiState.isTopBarOpen) {
            dispatchUiStateAction({ type: 'setIsTopBarOpen', isTopBarOpen: false });
        }
    }

    return (
        <AvatarBlock
            backgroundColor="white"
            badgeText={!props.isActiveEmployment ? translateKey('common.inactivePartner') : ''}
            imageUrl={props.practitioner.profilePictureUrl}
            initials={props.practitioner && getDisplayInitials(props.practitioner)}
            size={props.size}
            subtitle={
                hasOneAuthorization(ManagerAuthorization.organizationRead) ? (
                    <Link
                        color="subtle"
                        fontWeight="normal"
                        onClick={handleLinkClick}
                        textAlign={props.size === 'large' ? 'center' : undefined}
                        to={`/organizations/${props.organization.id}/view`}
                        variant="link"
                    >
                        {props.organization.name}
                    </Link>
                ) : (
                    <>{props.organization.name}</>
                )
            }
            subtitleSuffix={isManager && props.organization.txcsId ? ` - ${props.organization.txcsId}` : undefined}
            title={
                hasOneAuthorization(ManagerAuthorization.practitionerRead) ? (
                    <Link
                        color="subtle"
                        fontWeight="medium"
                        onClick={handleLinkClick}
                        to={`/practitioners/${props.practitioner.id}/profile`}
                        variant="link"
                    >
                        {getDisplayName(props.practitioner)}
                    </Link>
                ) : (
                    <>{getDisplayName(props.practitioner)}</>
                )
            }
            titleSuffix={isManager && props.practitioner.uid ? ` - ${props.practitioner.uid}` : undefined}
        />
    );
}
